import * as Sentry from '@sentry/react';
import { ApolloLink } from 'apollo-link';
import { ApolloClient } from 'apollo-client';
import { InMemoryCache } from 'apollo-cache-inmemory';
import { HttpLink } from 'apollo-link-http';
import { persistCache } from 'apollo-cache-persist';
import { onError } from 'apollo-link-error';
import { getStore } from './configureStore';
import { signOutSuccess } from './containers/SignIn/actions';
import { authTokenSelector, selectedRootEntitiesSelector } from './containers/SignIn/selectors';
import errorFactory from './utils/errorFactory';
import { lastActivitySet } from './utils/activity';
import { addError } from './containers/GlobalMessage/actions';
import { isRefreshLocked } from './utils/authLock';
import { subscribe } from './utils/authentication';

class ApolloApiCallError extends Error {
  constructor(name, message, info) {
    super();
    this.name = name;
    this.message = name;
    this.info = info;
  }
}

export default function configureApollo(client = 'users') {
  const store = getStore();
  const isUserClient = client === 'users';

  // setup cache and rehydrate
  const cache = new InMemoryCache();
  persistCache({ cache, storage: window.localStorage });

  const graphqlClients = {
    users: process.env.REACT_APP_API_CLIENT_URL,
    patients: process.env.REACT_APP_API_PATIENTS_CLIENT_URL,
  };

  const link = new HttpLink({
    uri: graphqlClients[client],
  });

  const backstageServiceLink = new HttpLink({
    uri: process.env.REACT_APP_API_BACKSTAGE_SERVICE_CLIENT_URL,
  });

  const linkManager = new ApolloLink((operation, forward) => {
    const { client: clientName } = operation.getContext();

    let selectedLink = link;
    if (clientName === 'backstage-service') {
      selectedLink = backstageServiceLink;
    }

    return selectedLink.request(operation, forward);
  });

  // setup auth middle ware for apollo to pull auth token out of localStorage
  // for authentication
  const authMiddleware = new ApolloLink((operation, forward) => {
    const { client: clientName } = operation.getContext();
    const headers = { 'Login-Origin': 'BACKSTAGE' };
    const token = authTokenSelector(store.getState());
    const selectedRootEntities = selectedRootEntitiesSelector(store.getState());

    const refreshBlacklist = ['refreshToken', 'GetChatAlerts', 'GetUserReports', 'inPlatformNotifications'];
    const isBlacklistedOperation = refreshBlacklist.includes(operation.operationName);

    const context = operation.getContext();

    if (isUserClient && clientName !== 'backstage-service') {
      headers['root-entities'] = selectedRootEntities;
    }

    if (context.phone) {
      headers.phone = context.phone;
    }

    if (context.resetToken) {
      headers['reset-token'] = context.resetToken;
    }

    if (context.patientUuid) {
      headers['patient-uuid'] = context.patientUuid;
    }

    if (token && isUserClient) {
      headers.authorization = `Bearer ${token}`;

      if (!isBlacklistedOperation) {
        lastActivitySet();
      }
    }

    operation.setContext({ headers });

    return forward(operation);
  });

  const errorMiddleware = onError((apolloLinkError) => {
    const navigate = (path) => {
      window.location.href = path;
    };

    const errors = errorFactory(apolloLinkError, navigate);

    errors.forEach(({ status, message = {}, error = {} } = {}) => {
      if (message.id !== 'app.errors.unexpected' && message.id !== 'app.error.network') {
        store.dispatch(addError(message, error));
      }

      if (status !== 401 && message.id !== 'app.errors.unexpected' && message.id !== 'app.error.network') {
        const sentryError = (error && error.path) || (message && message.id) || 'Error occurred';

        Sentry.captureException(new ApolloApiCallError(sentryError, sentryError, apolloLinkError), {
          extra: {
            displayed: message.id || 'API Error',
            apolloError: true,
            ...error,
          },
        });
      }

      if (status === 401 && isUserClient && !isRefreshLocked()) {
        store.dispatch(signOutSuccess());
      }
    });
  });

  // ApolloClient
  const apolloClient = new ApolloClient({
    link: ApolloLink.from([authMiddleware, errorMiddleware, linkManager]),
    cache,
    connectToDevTools: true,
    dataIdFromObject: (object) => object.id,
  });
  subscribe((sessionAuth) => {
    if (!sessionAuth.get('token')) {
      apolloClient.clearStore();
    }
  });
  return apolloClient;
}
